import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import Draggable from "react-draggable";
import { useTranslation } from "react-i18next";
import { MdOutlineAddCircleOutline } from "react-icons/md";
import { TbTrash, TbTrashOff } from "react-icons/tb";
import Select from "react-select";
import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import swal from "sweetalert";
import {
  customOption,
  customSingleValue,
  LoadingSpinner,
} from "../../../constants/const";
import { encryptStorage } from "../../../constants/EncryptStorage";
import { ApiFinance } from "../../config/finance/ApiFinance";
import { GetLanguageString } from "../../helper/Components";
import { GetCustomStyles } from "../../helper/Method";
import { ApiFinance as ApiFinanceLocal } from "../ApiFinance";
import { getMinMaxDates, validateTransactionDate } from "../fiscalYearHelper";

const CustomSelect = ({
  label,
  options,
  value,
  onChange,
  loading,
  error,
  placeholder,
  disabled = false,
}) => (
  <FormGroup>
    <Label className="col-form-label">
      <GetLanguageString props={label} />
    </Label>
    <Select
      components={{ Option: customOption, SingleValue: customSingleValue }}
      options={options}
      value={value}
      className="basic-select no-drag"
      classNamePrefix="select"
      isLoading={loading}
      isDisabled={disabled || options.length === 0}
      onChange={onChange}
      placeholder={placeholder}
      {...(error && { invalid: true })}
    />
    {error && <h4 className="text-error">{error}</h4>}
  </FormGroup>
);

const CustomInput = ({ label, value, onChange, error, ...rest }) => (
  <FormGroup>
    <Label className="col-form-label">
      <GetLanguageString props={label} />
    </Label>
    <Input type="text" value={value} onChange={onChange} className="no-drag" invalid={!!error} {...rest} />
    {error && <h4 className="text-error no-drag">{error}</h4>}
  </FormGroup>
);

const getCleanNumber = (value) => String(value || "").replace(/[^0-9.]/g, "");

const getReceiptNumber = (...values) => {
  for (const value of values) {
    if (!value) continue;
    const str = String(value).trim();
    if (str) {
      const parts = str.split(" ");
      return parts.length > 1 ? parts.slice(1).join(" ") : str;
    }
  }
  return "";
};

const cleanSerialNumber = (val) => {
  const str = String(val || "").trim();
  const match = str.match(/^(\d+)/);
  return match ? match[1] : str.replace(/\s*[-()].*$/, "").trim();
};

const getAmount = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined || value === "") continue;

    const cleanValue = String(value).replace(/,/g, "").trim();
    const numberValue = Number(cleanValue);

    if (!Number.isNaN(numberValue)) {
      return String(Math.abs(numberValue));
    }

    return cleanValue;
  }
  return "";
};

const getDateValue = (value) => {
  if (!value) return new Date();
  if (value instanceof Date) return value;

  const raw = String(value).trim();
  const parts = raw.split(/[-/]/).map(Number);

  if (parts.length === 3 && parts.every((part) => !Number.isNaN(part))) {
    if (String(parts[0]).length === 4) {
      return new Date(parts[0], parts[1] - 1, parts[2]);
    }
    return new Date(parts[2], parts[1] - 1, parts[0]);
  }

  const parsedDate = new Date(raw);
  return Number.isNaN(parsedDate.getTime()) ? new Date() : parsedDate;
};

const getTransactionMemo = (trans) => {
  if (!trans) return "";
  if (trans.memo && String(trans.memo).trim()) return String(trans.memo).trim();
  if (trans.lineMemo && String(trans.lineMemo).trim()) return String(trans.lineMemo).trim();
  if (Array.isArray(trans.details) && trans.details.length > 0) {
    for (const detail of trans.details) {
      if (detail?.memo && String(detail.memo).trim()) return String(detail.memo).trim();
      if (detail?.lineMemo && String(detail.lineMemo).trim()) return String(detail.lineMemo).trim();
    }
  }
  return "";
};

const getMemoParts = (memo) => {
  const value = String(memo || "").trim();
  const match = value.match(/^[-]?(\d+)\s*x\s*(\d+)/i);

  if (!match) {
    return { memo: value, quantity: "", price: "" };
  }

  let quantity = String(parseInt(match[1], 10));
  let price = String(parseInt(match[2], 10));

  let afterPrice = value.split(/x\s*\d+/i)[1] || "";
  let memoPart = afterPrice.replace(/[()]/g, "").trim();

  return {
    quantity,
    price,
    memo: memoPart || "",
  };
};

const DEFAULT_FORM = {
  date: new Date(),
  receiptNumber: "",
  memo: "",
  entries: [
    {
      selectedCategory: null,
      financeAccount: null,
      dimension2: null,
      accounts: [],
      dimensions: [],
      amount: "",
      quantity: "",
      price: "",
      lineMemo: "",
      serialNumber: "",
    },
  ],
};

const isSpecialCategory = (categoryCode) => {
  return categoryCode === "loan_individual" || categoryCode === "infaq_rafeeqat";
};

const categorySupportsQtyPrice = (categoryCode) => {
  return categoryCode === "asset" || categoryCode === "maktaba";
};

const EditOtherIncome = ({
  setModal,
  transactionNo,
  transaction,
  unitId,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const toggle = () => setModal(false);

  const unitTypeName = encryptStorage.getItem("unitTypeName");

  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [financeCategories, setFinanceCategories] = useState([]);
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState({
    source: false,
    submit: false,
  });

  const isBusy = Object.values(loading).some(Boolean);

  const isDigitalReceipt = /[a-zA-Z]/.test(formData.receiptNumber || "");

  const handleDrag = (e, ui) => {
    setPosition((prev) => ({
      x: prev.x + ui.deltaX,
      y: prev.y + ui.deltaY,
    }));
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleReceiptNumber = (e) => {
    handleChange("receiptNumber", getCleanNumber(e.target.value));
  };

  const fetchAccountsAndDimensions = (index, categoryId) => {
    if (!categoryId) return;

    ApiFinance()
      .GetFinanceSubCategoryAccountsById(categoryId)
      .then((res) => {
        setFormData((prev) => {
          const updated = prev.entries.map((entry, idx) => {
            if (idx === index) {
              const matchedAcc = entry.financeAccount && (res.data || []).find(x => x.value === entry.financeAccount.value || x.label === entry.financeAccount.label);
              return {
                ...entry,
                accounts: res.data || [],
                financeAccount: matchedAcc || res.data?.[0] || null,
              };
            }
            return entry;
          });
          return { ...prev, entries: updated };
        });
      })
      .catch(console.error);

    ApiFinance()
      .GetFinanceSubCategoryDimensionsById(categoryId)
      .then((res) => {
        setFormData((prev) => {
          const updated = prev.entries.map((entry, idx) => {
            if (idx === index) {
              const matchedDim = entry.dimension2 && (res.data || []).find(x => x.value === entry.dimension2.value || x.label === entry.dimension2.label);
              return {
                ...entry,
                dimensions: res.data || [],
                dimension2: matchedDim || res.data?.[0] || null,
              };
            }
            return entry;
          });
          return { ...prev, entries: updated };
        });
      })
      .catch(console.error);
  };

  const handleEntryChange = (index, field, value) => {
    setFormData((prev) => {
      const updatedEntries = prev.entries.map((entry, idx) => {
        if (idx === index) {
          const updated = { ...entry, [field]: value };

          if (field === "selectedCategory") {
            updated.financeAccount = null;
            updated.dimension2 = null;
            updated.accounts = [];
            updated.dimensions = [];
            updated.serialNumber = "";
            const isQtyPrice = categorySupportsQtyPrice(value?.label1);
            if (!isQtyPrice) {
              updated.quantity = "";
              updated.price = "";
            }
            if (value?.value) {
              fetchAccountsAndDimensions(index, value.value);
            }
          }

          const categoryCode = updated.selectedCategory?.label1;
          if (categorySupportsQtyPrice(categoryCode)) {
            if (field === "quantity" || field === "price" || field === "selectedCategory") {
              const q = parseFloat(updated.quantity);
              const p = parseFloat(updated.price);
              if (!isNaN(q) && !isNaN(p)) {
                updated.amount = String(Math.abs(q * p));
              } else {
                updated.amount = "";
              }
            }
          }

          if (field === "amount" && value) {
            updated.amount = String(Math.abs(parseFloat(value) || 0));
          }

          return updated;
        }
        return entry;
      });
      return { ...prev, entries: updatedEntries };
    });

    setErrors((prev) => {
      const updatedEntries = Array.isArray(prev.entries) ? [...prev.entries] : [];
      if (updatedEntries[index]) {
        if (field === "selectedCategory") {
          updatedEntries[index] = {
            selectedCategory: "",
            financeAccount: "",
            dimension2: "",
            amount: "",
            quantity: "",
            price: "",
            serialNumber: "",
          };
        } else {
          updatedEntries[index] = {
            ...updatedEntries[index],
            [field]: "",
          };
        }
      }
      return { ...prev, entries: updatedEntries };
    });
  };

  const addEntry = () => {
    const defaultCategory = financeCategories[0] || null;
    const nextIndex = formData.entries.length;
    setFormData((prev) => ({
      ...prev,
      entries: [
        ...prev.entries,
        {
          isNew: true,
          selectedCategory: defaultCategory,
          financeAccount: null,
          dimension2: null,
          accounts: [],
          dimensions: [],
          amount: "",
          quantity: "",
          price: "",
          lineMemo: "",
          serialNumber: "",
        },
      ],
    }));
    setErrors((prev) => {
      if (Array.isArray(prev.entries)) {
        return {
          ...prev,
          entries: [...prev.entries, {}]
        };
      }
      return prev;
    });
    if (defaultCategory?.value) {
      fetchAccountsAndDimensions(nextIndex, defaultCategory.value);
    }
  };

  const removeEntry = (index) => {
    setFormData((prev) => ({
      ...prev,
      entries: prev.entries.filter((_, idx) => idx !== index),
    }));
    setErrors((prev) => {
      if (Array.isArray(prev.entries)) {
        return {
          ...prev,
          entries: prev.entries.filter((_, idx) => idx !== index)
        };
      }
      return prev;
    });
  };

  const getTotalAmount = (entries) => {
    const total = entries.reduce(
      (sum, entry) => sum + (parseFloat(entry.amount) || 0),
      0,
    );
    return Number.isInteger(total) ? total.toString() : total.toFixed(2);
  };

  // Fetch categories on mount
  useEffect(() => {
    setLoading((prev) => ({ ...prev, source: true }));
    ApiFinance()
      .GetFinanceSubCategoriesListByType("income")
      .then((res) => {
        const filtered = (res.data || []).filter((cat) => {
          if (
            cat.label1.includes("markaz") ||
            cat.label1.includes("tanzeem") ||
            cat.label1.includes("help") ||
            cat.label1.includes("reimbursment")
          ) {
            return false;
          }
          if (unitTypeName !== "Halqa" && cat.label1.includes("course")) {
            return false;
          }
          if (
            unitTypeName !== "Halqa" &&
            (cat.label1?.toLowerCase().includes("maktaba") ||
              (cat.label || cat.name || cat.category || "").toLowerCase().includes("maktaba"))
          ) {
            return false;
          }
          if (cat.label1 === "infaq") {
            return false;
          }
          return true;
        });

        const formatted = filtered.map((c) => ({
          value: c.value || c.id,
          label: c.label || c.category,
          label1: c.label1 || c.code,
        }));
        setFinanceCategories(formatted);
      })
      .catch(console.error)
      .finally(() => {
        setLoading((prev) => ({ ...prev, source: false }));
      });
  }, [unitTypeName]);

  // Load existing transaction
  useEffect(() => {
    if (!transaction) return;

    const defaultMemo = getTransactionMemo(transaction);
    const hasDetails = transaction.details && transaction.details.length > 0;

    if (hasDetails) {
      // Filter negative details (income lines) and exclude positive balancing lines
      const negativeDetails = transaction.details.filter((detail) => (detail.amount || 0) < 0);

      const entriesData = negativeDetails.map((detail) => {
        const detailMemo = detail.memo || detail.lineMemo || defaultMemo;
        const memoParts = getMemoParts(detailMemo);
        const rawLabel = String(detail.category.label || detail.category.category || "").replace(/\s*\([^)]*\)/, "").trim();
        const mappedCat = detail.category ? {
          value: detail.category.value || detail.category.id,
          label: rawLabel,
          label1: detail.category.label1 || detail.category.code,
        } : null;
        return {
          id: detail.id || null,
          selectedCategory: mappedCat,
          financeAccount: detail.account ? { value: parseInt(detail.account), label: detail.accountName } : null,
          dimension2: detail.dimension2Id ? { value: parseInt(detail.dimension2Id), label: detail.dimension2Name } : null,
          accounts: [],
          dimensions: [],
          amount: String(Math.abs(detail.amount || 0)),
          quantity: detail.quantity !== undefined && detail.quantity !== null ? String(detail.quantity) : (memoParts.quantity || ""),
          price: detail.price !== undefined && detail.price !== null ? String(detail.price) : (memoParts.price || ""),
          lineMemo: memoParts.memo || detailMemo,
          serialNumber: cleanSerialNumber(detail.personID || detail.personId || detail.serialNumber || transaction?.personID || transaction?.personId || transaction?.serialNumber),
        };
      });

      setFormData({
        date: getDateValue(transaction.date),
        memo: defaultMemo,
        receiptNumber: getReceiptNumber(transaction.receiptNumber, transaction.reference),
        entries: entriesData,
      });

      entriesData.forEach((entry, index) => {
        if (entry.selectedCategory?.value) {
          fetchAccountsAndDimensions(index, entry.selectedCategory.value);
        }
      });
    } else {
      const memoParts = getMemoParts(defaultMemo);
      const mappedCat = transaction.category ? {
        value: transaction.category.value || transaction.category.id,
        label: transaction.category.label || transaction.category.category,
        label1: transaction.category.label1 || transaction.category.code,
      } : null;
      const singleEntry = {
        selectedCategory: mappedCat,
        financeAccount: transaction.codeId ? { value: parseInt(transaction.codeId), label: transaction.accountName } : null,
        dimension2: transaction.dimension2Id ? { value: parseInt(transaction.dimension2Id), label: transaction.subHeadName } : null,
        accounts: [],
        dimensions: [],
        amount: getAmount(transaction.amount),
        quantity: transaction.quantity !== undefined && transaction.quantity !== null ? String(transaction.quantity) : (memoParts.quantity || ""),
        price: transaction.price !== undefined && transaction.price !== null ? String(transaction.price) : (memoParts.price || ""),
        lineMemo: memoParts.memo || defaultMemo,
        serialNumber: cleanSerialNumber(transaction?.personID || transaction?.personId || transaction?.serialNumber),
      };

      setFormData({
        date: getDateValue(transaction.date),
        memo: defaultMemo,
        receiptNumber: getReceiptNumber(transaction.receiptNumber, transaction.reference),
        entries: [singleEntry],
      });

      if (singleEntry.selectedCategory?.value) {
        fetchAccountsAndDimensions(0, singleEntry.selectedCategory.value);
      }
    }
  }, [transaction]);

  const validate = () => {
    const temp = {};
    const entriesErrors = [];
    let hasInvalidEntry = false;

    for (let i = 0; i < formData.entries.length; i++) {
      const entry = formData.entries[i];
      const entryErrors = {};

      if (!entry.selectedCategory) {
        entryErrors.selectedCategory = t("common_field_required_error");
        hasInvalidEntry = true;
      } else {
        const categoryCode = entry.selectedCategory.label1;
        const isSpecial = isSpecialCategory(categoryCode);
        const isQtyPrice = categorySupportsQtyPrice(categoryCode);

        if (!isQtyPrice) {
          if (!entry.amount || String(entry.amount).trim() === "") {
            entryErrors.amount = t("common_field_required_error");
            hasInvalidEntry = true;
          } else {
            const parsedAmount = parseFloat(entry.amount);
            if (isNaN(parsedAmount) || parsedAmount <= 0) {
              entryErrors.amount = t("amount_must_be_greater_than_zero");
              hasInvalidEntry = true;
            }
          }
        }

        if (!isSpecial) {
          if (!entry.financeAccount) {
            entryErrors.financeAccount = t("common_field_required_error");
            hasInvalidEntry = true;
          }
        }

        const showSubHead = categoryCode !== "tarbiati_course" && categoryCode !== "misc" && !isSpecial;
        if (showSubHead) {
          if (!entry.dimension2) {
            entryErrors.dimension2 = t("common_field_required_error");
            hasInvalidEntry = true;
          }
        }

        if (isQtyPrice) {
          if (!entry.quantity || String(entry.quantity).trim() === "") {
            entryErrors.quantity = t("common_field_required_error");
            hasInvalidEntry = true;
          } else {
            const parsedQty = parseFloat(entry.quantity);
            if (isNaN(parsedQty) || parsedQty <= 0) {
              entryErrors.quantity = t("quantity_must_be_greater_than_zero");
              hasInvalidEntry = true;
            }
          }

          if (!entry.price || String(entry.price).trim() === "") {
            entryErrors.price = t("common_field_required_error");
            hasInvalidEntry = true;
          } else {
            const parsedPrice = parseFloat(entry.price);
            if (isNaN(parsedPrice) || parsedPrice <= 0) {
              entryErrors.price = t("price_must_be_greater_than_zero");
              hasInvalidEntry = true;
            }
          }
        }

        if (categoryCode === "loan_individual") {
          if (!entry.serialNumber || !String(entry.serialNumber).trim()) {
            entryErrors.serialNumber = t("common_field_required_error");
            hasInvalidEntry = true;
          }
        }
      }

      entriesErrors.push(entryErrors);
    }

    if (!formData.memo || !formData.memo.trim()) {
      temp.memo = t("common_field_required_error");
      hasInvalidEntry = true;
    }

    const dateError = validateTransactionDate(formData.date, transaction?.date);
    if (dateError) {
      temp.date = t(dateError);
    }

    temp.entries = hasInvalidEntry ? entriesErrors : [];

    temp.receiptNumber = formData.receiptNumber ? "" : t("common_field_required_error");

    setErrors({
      ...temp,
    });

    const isReceiptValid = !!formData.receiptNumber;
    return isReceiptValid && !hasInvalidEntry && !temp.date;
  };

  const handleSubmit = (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!validate()) return;

    const totalAmount = getTotalAmount(formData.entries);

    const entriesPayload = formData.entries.map((entry) => {
      const categoryCode = entry.selectedCategory?.label1;
      const isQtyPrice = categorySupportsQtyPrice(categoryCode);
      const fallbackMemo = entry.lineMemo || formData.memo;
      return {
        category: entry.selectedCategory ? {
          value: entry.selectedCategory.value,
          label: entry.selectedCategory.label,
          label1: entry.selectedCategory.label1
        } : null,
        financeAccount: entry.financeAccount ?? null,
        dimension2: entry.dimension2 ?? null,
        amount: entry.amount,
        lineMemo: fallbackMemo,
        quantity: isQtyPrice && entry.quantity ? parseInt(entry.quantity, 10) : null,
        price: isQtyPrice && entry.price ? parseFloat(entry.price) : null,
        serialNumber: entry.serialNumber || serialNumber || "",
      };
    });

    const loanRow = formData.entries.find(e => e.selectedCategory?.label1 === "loan_individual");
    const serialNumber = loanRow ? loanRow.serialNumber : "";

    const dateObj = new Date(formData.date);
    const isoDate = !isNaN(dateObj.getTime()) ? dateObj.toISOString() : formData.date;

    const payload = {
      transactionNo,
      category: entriesPayload[0]?.category ?? null,
      date: isoDate,
      amount: totalAmount,
      serialNumber: serialNumber,
      memo: formData.memo || entriesPayload[0]?.lineMemo || getTransactionMemo(transaction),
      unitId: unitId || transaction?.unitId,
      receiptNumber: formData.receiptNumber,
      entries: entriesPayload,
      quantity: entriesPayload[0]?.quantity ?? null,
      price: entriesPayload[0]?.price ?? null,
    };

    if (entriesPayload.length === 1) {
      payload.financeAccount = entriesPayload[0].financeAccount;
      payload.dimension2 = entriesPayload[0].dimension2;
    }

    setLoading((prev) => ({ ...prev, submit: true }));

    ApiFinanceLocal()
      .EditOtherIncome(payload)
      .then((res) => {
        const data = res.data || {};

        if (Number(data.statusCode) !== 200) {
          const isWarning = Number(data.statusCode) === 300 || data.message === "modification_request_already_pending";
          swal({
            title: isWarning ? t("common_warning") || "Warning" : "Error",
            text: t(data.message) || data.message || "Failed to update Other Income",
            icon: isWarning ? "warning" : "error",
            buttons: "OK",
          }).then(() => {
            if (isWarning) {
              setModal(false);
            }
          });
          return;
        }

        swal({
          title: "Success",
          text: t(data.message) || t("member_member_detail_fund_edited"),
          icon: "success",
          buttons: "OK",
        }).then(() => {
          setModal(false);
          if (typeof onSuccess === "function") {
            onSuccess();
          }
        });
      })
      .catch((err) => {
        console.error(err);
        swal({
          title: "Error",
          text: err.response?.data || "Unable to update Other Income.",
          icon: "error",
          buttons: "OK",
        });
      })
      .finally(() => {
        setLoading((prev) => ({ ...prev, submit: false }));
      });
  };

  return (
    <Draggable position={position} onDrag={handleDrag} handle=".modal-header" cancel=".no-drag">
      <Modal isOpen className="inmodal" size="xl" style={{ maxWidth: "1350px", width: "95%" }} autoFocus={false}>
        <ModalHeader className="modal-title" tag="h4">
          <GetLanguageString props="edit_infaq" />
        </ModalHeader>

        {loading.source || isBusy ? (
          <ModalBody className="text-center">
            <LoadingSpinner />
          </ModalBody>
        ) : (
          <Form onSubmit={handleSubmit} noValidate>
            <ModalBody>
              {/* Common Fields Row */}
              <Row className="mb-3">
                <Col md="3">
                  <FormGroup>
                    <Label className="col-form-label">
                      <GetLanguageString props="funds_date" />
                    </Label>
                    <DatePicker
                      showYearDropdown
                      dropdownMode="select"
                      placeholderText="dd/mm/yyyy"
                      className="form-control no-drag"
                      selected={formData.date}
                      minDate={getMinMaxDates(formData.date, transaction?.date).minDate}
                      maxDate={getMinMaxDates(formData.date, transaction?.date).maxDate}
                      onChange={(date) =>
                        handleChange("date", date ?? new Date())
                      }
                      dateFormat="dd/MM/yyyy"
                    />
                    {errors.date && (
                      <h4 className="text-error">{errors.date}</h4>
                    )}
                  </FormGroup>
                </Col>

                <Col md="3">
                  <FormGroup>
                    <Label className="col-form-label">
                      <GetLanguageString props="funds_receipt_number" />
                    </Label>
                    <Input
                      autoFocus
                      type="text"
                      className="no-drag"
                      value={formData.receiptNumber}
                      onChange={handleReceiptNumber}
                      disabled={isDigitalReceipt}
                    />
                    {errors.receiptNumber && (
                      <h4 className="text-error">{errors.receiptNumber}</h4>
                    )}
                  </FormGroup>
                </Col>

                <Col md="3">
                  <CustomInput
                    label="transactions_memo"
                    value={formData.memo}
                    onChange={(e) => handleChange("memo", e.target.value)}
                    error={errors.memo}
                  />
                </Col>

                <Col md="3">
                  <FormGroup>
                    <Label className="col-form-label">
                      <GetLanguageString props="funds_add_fund_amount" />
                    </Label>
                    <Input
                      type="text"
                      className="no-drag"
                      disabled
                      value={getTotalAmount(formData.entries)}
                    />
                  </FormGroup>
                </Col>
              </Row>

              {/* Entries Box */}
              <div className="ibox">
                <div className="ibox-title d-flex justify-content-between align-items-center">
                  <h5>
                    <GetLanguageString props="finance_other_income_entry_details" />
                  </h5>
                  <MdOutlineAddCircleOutline
                    size={25}
                    color="#3ba147"
                    onClick={addEntry}
                    style={{ marginRight: 6, marginBottom: 1, cursor: "pointer" }}
                  />
                </div>
                <div className="ibox-content">
                  {formData.entries.map((entry, index) => {
                    const categoryCode = entry.selectedCategory?.label1;
                    const isLoanIndividual = categoryCode === "loan_individual";
                    const isAssetOrMaktaba = categoryCode === "asset" || categoryCode === "maktaba";
                    const isSpecial = isSpecialCategory(categoryCode);
                    const isQtyPrice = categorySupportsQtyPrice(categoryCode);
                    const isFrontendAdded = transaction?.isFrontendAdded ?? (categoryCode !== "maktaba" && categoryCode !== "misc" && categoryCode !== "loan_markaz" && categoryCode !== "help_from_muqami_tanzeem" && categoryCode !== "tarbiati_course_reimbursment_from_markaz");
                    const disableIncomeHead = !isFrontendAdded || categoryCode === "maktaba" || categoryCode === "misc";
                    const disableIncomeSubHead = !isFrontendAdded || !entry.selectedCategory;
                    const showIncomeSubHead = categoryCode !== "tarbiati_course" && categoryCode !== "misc" && !isSpecial;
                    const isMaktaba = categoryCode === "maktaba";

                    return (
                      <Row key={index} className="mb-3 align-items-start">
                        <Col md>
                          <div
                            onClick={() => {
                              if (!entry.isNew) {
                                swal({
                                  title: t("finance_category_change_notice_title") || "Change Category",
                                  text: t("finance_category_change_notice_text") || "To change the category of an existing entry, please delete this row and add a new row with your desired category.",
                                  icon: "info",
                                  buttons: "OK",
                                });
                              }
                            }}
                          >
                            <CustomSelect
                              name={`category-${index}`}
                              label="finance_tab_category"
                              options={financeCategories}
                              value={entry.selectedCategory}
                              onChange={(val) =>
                                handleEntryChange(index, "selectedCategory", val)
                              }
                              error={errors.entries?.[index]?.selectedCategory}
                              isDisabled={!entry.isNew}
                              placeholder={t("member_select")}
                            />
                          </div>
                        </Col>

                        {isLoanIndividual && (
                          <Col md>
                            <CustomInput
                              name={`serialNumber-${index}`}
                              label="notifications_rafiq_serial_number"
                              value={entry.serialNumber || ""}
                              onChange={(e) =>
                                handleEntryChange(index, "serialNumber", e.target.value)
                              }
                              error={errors.entries?.[index]?.serialNumber}
                            />
                          </Col>
                        )}

                        {!isSpecial && (
                          <Col md>
                            <CustomSelect
                              name={`financeAccount-${index}`}
                              label="funds_income_head"
                              options={entry.accounts || []}
                              value={entry.financeAccount}
                              onChange={(val) =>
                                handleEntryChange(index, "financeAccount", val)
                              }
                              error={errors.entries?.[index]?.financeAccount}
                              isDisabled={disableIncomeHead || !entry.selectedCategory}
                              placeholder={
                                !entry.selectedCategory
                                  ? t("finance_other_income_income_heads_select")
                                  : (entry.accounts || []).length > 0
                                    ? t("finance_other_income_income_heads_select")
                                    : t("finance_other_income_income_heads_empty")
                              }
                            />
                          </Col>
                        )}

                        {showIncomeSubHead && (
                          <Col md>
                            <CustomSelect
                              name={`dimension2-${index}`}
                              label="funds_income_sub_head"
                              options={entry.dimensions || []}
                              value={entry.dimension2}
                              onChange={(val) =>
                                handleEntryChange(index, "dimension2", val)
                              }
                              error={errors.entries?.[index]?.dimension2}
                              isDisabled={disableIncomeSubHead}
                              placeholder={
                                !entry.selectedCategory
                                  ? t("finance_other_income_income_sub_heads_select")
                                  : (entry.dimensions || []).length > 0
                                    ? t("finance_other_income_income_sub_heads_select")
                                    : t("finance_other_income_income_sub_heads_empty")
                              }
                            />
                          </Col>
                        )}

                        <Col md={isAssetOrMaktaba ? "1" : null}>
                          <CustomInput
                            name={`entryAmount-${index}`}
                            label="funds_add_fund_amount"
                            value={entry.amount}
                            onChange={(e) =>
                              handleEntryChange(index, "amount", e.target.value)
                            }
                            error={errors.entries?.[index]?.amount}
                            readOnly={isQtyPrice}
                            disabled={isQtyPrice}
                          />
                        </Col>

                        {isQtyPrice && (
                          <Col md="1">
                            <CustomInput
                              name={`quantity-${index}`}
                              label="finance_other_income_quantity"
                              value={entry.quantity}
                              onChange={(e) =>
                                handleEntryChange(index, "quantity", e.target.value)
                              }
                              error={errors.entries?.[index]?.quantity}
                            />
                          </Col>
                        )}

                        {isQtyPrice && (
                          <Col md={isMaktaba ? "2" : "1"}>
                            <CustomInput
                              name={`price-${index}`}
                              label="finance_other_income_price"
                              value={entry.price}
                              onChange={(e) =>
                                handleEntryChange(index, "price", e.target.value)
                              }
                              error={errors.entries?.[index]?.price}
                            />
                          </Col>
                        )}

                        {!isMaktaba && (
                          <Col md>
                            <CustomInput
                              name={`lineMemo-${index}`}
                              label="transactions_memo"
                              value={entry.lineMemo}
                              onChange={(e) =>
                                handleEntryChange(index, "lineMemo", e.target.value)
                              }
                              error={errors.entries?.[index]?.lineMemo}
                            />
                          </Col>
                        )}

                        <Col md="1" className="text-center">
                          <Label className="col-form-label d-block">&nbsp;</Label>
                          {formData.entries.length === 1 ? (
                            <TbTrashOff
                              size={25}
                              color="#a72424"
                              style={{ opacity: 0.5 }}
                            />
                          ) : (
                            <TbTrash
                              size={25}
                              color="#bb0e0e"
                              style={{ cursor: "pointer" }}
                              onClick={() => removeEntry(index)}
                            />
                          )}
                        </Col>
                      </Row>
                    );
                  })}
                </div>
              </div>
            </ModalBody>

            <ModalFooter>
              <Button
                color="white"
                className={GetCustomStyles().btn_style}
                onClick={toggle}
              >
                <GetLanguageString props="common_cancel" />
              </Button>
              <Button
                color="primary"
                className={GetCustomStyles().btn_style}
                disabled={isBusy}
                type="submit"
              >
                <GetLanguageString props="common_save_changes" />
              </Button>
            </ModalFooter>
          </Form>
        )}
      </Modal>
    </Draggable>
  );
};

export default EditOtherIncome;
